/**
 * Leaf Rating System
 *
 * Two dimensions:
 * 1. Leaf Count (1-3): Based on section categorization of vegan options
 *    - 1 leaf: Only sides/soups (no entrees), or <=3 items (no sections)
 *    - 2 leaves: At least one entree, or 4-5 items (no sections)
 *    - 3 leaves: Multiple entrees (2+) AND another food category, or >5 items (no sections)
 *
 * 2. Leaf Color - Verification Tier:
 *    - Gray (B&W): Ad hoc search with only ML-predicted items (no GUARANTEED_VEGAN)
 *    - Green outline: Ad hoc with GUARANTEED_VEGAN items OR regular saved restaurants
 *    - Solid green: Verified restaurants (details: true)
 */

// Section categorization keywords (word boundary matching)
const ENTREE_KEYWORDS = [
  "entree",
  "entrees",
  "main",
  "mains",
  "main course",
  "platos fuertes",
  "dinner",
  "lunch",
  // Asian / global cuisine entree-equivalent sections
  "stir fried",
  "stir fry",
  "curries",
  "curry",
  "noodles",
  "noodle",
  "fried rice",
  "wok",
  "rice plates",
  "specialty",
  "specialties",
  "specialities",
  // Dedicated vegan sections on non-vegan restaurants are entree-equivalent
  "vegan dishes",
  "vegan options",
  "vegan entrees",
  "vegan menu",
  "plant based",
  "plant-based",
];
const APPETIZER_KEYWORDS = [
  "appetizer",
  "appetizers",
  "starter",
  "starters",
  "aperitivos",
  "small plates",
];
const SIDE_KEYWORDS = ["side", "sides", "salad", "salads", "soup", "soups"];
const DESSERT_KEYWORDS = [
  "dessert",
  "desserts",
  "postre",
  "postres",
  "sweets",
];
const DRINK_KEYWORDS = [
  "drink",
  "drinks",
  "beverage",
  "beverages",
  "cocktail",
  "cocktails",
  "mocktail",
  "mocktails",
  "wine",
  "wines",
  "beer",
  "beers",
  "spirits",
  "coffee",
  "tea",
  "teas",
];

/**
 * Categorize a section name into entree, appetizer, side, dessert, drink, or unknown
 * @param {string} sectionName - The name of the menu section
 * @returns {'entree' | 'appetizer' | 'side' | 'dessert' | 'drink' | 'unknown'}
 */
export function categorizeSection(sectionName) {
  if (!sectionName || typeof sectionName !== "string") {
    return "unknown";
  }

  const normalized = sectionName.toLowerCase().trim();

  // Check for entree keywords (word boundary matching)
  for (const keyword of ENTREE_KEYWORDS) {
    const regex = new RegExp(`\\b${keyword}\\b`, "i");
    if (regex.test(normalized)) {
      return "entree";
    }
  }

  // Check for appetizer keywords
  for (const keyword of APPETIZER_KEYWORDS) {
    const regex = new RegExp(`\\b${keyword}\\b`, "i");
    if (regex.test(normalized)) {
      return "appetizer";
    }
  }

  // Check for side keywords
  for (const keyword of SIDE_KEYWORDS) {
    const regex = new RegExp(`\\b${keyword}\\b`, "i");
    if (regex.test(normalized)) {
      return "side";
    }
  }

  // Check for dessert keywords
  for (const keyword of DESSERT_KEYWORDS) {
    const regex = new RegExp(`\\b${keyword}\\b`, "i");
    if (regex.test(normalized)) {
      return "dessert";
    }
  }

  // Check for drink keywords
  for (const keyword of DRINK_KEYWORDS) {
    const regex = new RegExp(`\\b${keyword}\\b`, "i");
    if (regex.test(normalized)) {
      return "drink";
    }
  }

  return "unknown";
}

/**
 * Get the section name from an item, handling both subdocument and string formats
 * @param {Object} item - A menu item
 * @returns {string|null}
 */
function getSectionName(item) {
  if (!item.section) return null;

  // Handle subdocument format {name, order}
  if (typeof item.section === "object" && item.section.name) {
    return item.section.name;
  }

  // Handle legacy string format
  if (typeof item.section === "string") {
    return item.section;
  }

  return null;
}

/**
 * Calculate the leaf count (1-3) based on menu sections
 * @param {Array} allMenuItems - Array of all menu items (may have sections)
 * @param {Array} veganOptions - Array of vegan options (used for item count fallback)
 * @returns {1 | 2 | 3}
 */
export function calculateLeafCount(allMenuItems, veganOptions) {
  // Use allMenuItems for section analysis if available
  const itemsWithSections = allMenuItems || veganOptions || [];
  // Use veganOptions for item count heuristic (what users see)
  const displayedItems = veganOptions || allMenuItems || [];

  if (displayedItems.length === 0) {
    return 1;
  }

  // Collect unique sections and categorize them
  let hasSection = false;
  let hasRecognizedSection = false;
  let entreeCount = 0;
  let hasAppetizer = false;
  let hasSide = false;
  let hasDessert = false;

  for (const item of itemsWithSections) {
    const sectionName = getSectionName(item);
    if (sectionName) {
      hasSection = true;
      const category = categorizeSection(sectionName);

      if (category !== "unknown") {
        hasRecognizedSection = true;
      }

      if (category === "entree") {
        entreeCount++;
      } else if (category === "appetizer") {
        hasAppetizer = true;
      } else if (category === "side") {
        hasSide = true;
      } else if (category === "dessert") {
        hasDessert = true;
      }
    }
  }

  const itemCount = displayedItems.length;

  // If no section data OR sections exist but none are recognized,
  // fall back to item count heuristic (more forgiving)
  // Use veganOptions count since that's what users see displayed
  if (!hasSection || !hasRecognizedSection) {
    if (itemCount > 5) return 3;
    if (itemCount > 3) return 2;
    return 1;
  }

  // 3 leaves: Multiple entrees (2+) AND at least one other food category
  const hasOtherCategory = hasAppetizer || hasSide || hasDessert;
  if (entreeCount >= 2 && hasOtherCategory) {
    return 3;
  }

  // 2 leaves: At least one entree
  if (entreeCount >= 1) {
    // Item count floor: lots of items with entrees shouldn't be capped at 2
    if (itemCount > 8) return 3;
    return 2;
  }

  // 1 leaf: Only sides, salads, soups, desserts (no entrees)
  // But don't give 1 leaf to a restaurant with many items
  if (itemCount > 5) return 2;
  return 1;
}

/**
 * Calculate the verification tier based on restaurant data
 * @param {Object} restaurant - The restaurant object
 * @returns {'gray' | 'green-outline' | 'green-solid'}
 */
export function calculateVerificationTier(restaurant) {
  if (!restaurant) {
    return "green-outline";
  }

  const isAdHoc = restaurant.metadata?.adHocSearch === true;
  const hasDetails = restaurant.details === true;

  // Solid green: verified restaurant (has details)
  if (hasDetails && !isAdHoc) {
    return "green-solid";
  }

  // Gray: only for ad hoc search with no GUARANTEED_VEGAN items
  if (isAdHoc) {
    const hasGuaranteedVegan = restaurant.allMenuItems?.some(
      (item) => item.originalCategory === "GUARANTEED_VEGAN"
    );

    // Gray: ad hoc with only ML predictions
    if (!hasGuaranteedVegan) {
      return "gray";
    }
  }

  // Green outline: default for saved restaurants, or ad hoc with guaranteed vegan
  return "green-outline";
}

// Categories that count as "vegan" for leaf rating purposes
const VEGAN_CATEGORIES = ["GUARANTEED_VEGAN", "PROBABLY_VEGAN"];

/**
 * Filter menu items to only include vegan items (GUARANTEED_VEGAN or PROBABLY_VEGAN)
 * @param {Array} items - Array of menu items
 * @returns {Array}
 */
function filterVeganItems(items) {
  if (!items || !Array.isArray(items)) return [];
  return items.filter((item) => VEGAN_CATEGORIES.includes(item.originalCategory));
}

/**
 * Get the complete leaf rating for a restaurant
 * @param {Object} restaurant - The restaurant object
 * @returns {{count: 1 | 2 | 3, tier: 'gray' | 'green-outline' | 'green-solid'}}
 */
export function getLeafRating(restaurant) {
  const veganOptions = restaurant?.veganOptions;
  const hasVeganOptions = veganOptions && veganOptions.length > 0;

  // For saved restaurants, veganOptions is the curated authoritative list —
  // use it for section analysis. For ad hoc results (no veganOptions),
  // fall back to ML-classified allMenuItems filtered to probable/guaranteed.
  const sectionSource = hasVeganOptions
    ? veganOptions
    : filterVeganItems(restaurant?.allMenuItems);

  return {
    count: calculateLeafCount(sectionSource, veganOptions),
    tier: calculateVerificationTier(restaurant),
  };
}
